// Company-name normalizer (strict dedupe). ONE definition of "the same company name": case, accents, punctuation, trademark signs, "&" vs "and",
// a leading "The", trailing legal words (Inc, LLC, Ltd, Corp, Company ...) and a trailing location or ticker tag (", CC, Tx", ", TX", "(OTC: X)") never make two names different. The server mirror is api/_lib/companyName.js (a test keeps
// the two identical); import this one from the UI and the mirror from api/. Pure, no network, never writes.
//
//   normalizeCompanyName("The Bluehook Tool Company, LLC")  -> "bluehook tool"
//   companyKey("Pipe Market") === companyKey("PipeMarket")  -> true   (spaces ignored)
//   collapseCompanies(rows)    -> live rows only (deleted and merged-away dropped), name collisions collapsed into one survivor
//   splitMatches(term, rows)   -> { matches, similar }: real matches first, near-matches (typo, extra word) as "similar"

const LEGAL = new Set(['inc', 'incorporated', 'llc', 'l l c', 'ltd', 'limited', 'corp', 'corporation', 'co', 'company', 'lp', 'llp', 'plc', 'lc', 'pllc', 'na', 'holdings', 'group']);

const STATES = 'AL|AK|AZ|AR|CA|CO|CT|DE|DC|FL|GA|HI|ID|IL|IN|IA|KS|KY|LA|ME|MD|MA|MI|MN|MS|MO|MT|NE|NV|NH|NJ|NM|NY|NC|ND|OH|OK|OR|PA|RI|SC|SD|TN|TX|UT|VT|VA|WA|WV|WI|WY';
const STATE_TAG = new RegExp(String.raw`\s*,\s*(` + STATES + String.raw`)\.?\s*$`, 'i');
const COUNTY_TAG = /\s*,?\s+[a-z]{2}\s*$/i;
const TICKER_TAG = /\s*\((otc|otcqb|otcqx|nyse|nasdaq|tsx|tsxv|lse)[^)]*\)\s*$/i;

// A trailing location or ticker tag is not part of the name: "EOG Resources CC, Tx" (county + state), "Acme, TX", "Acme (OTC: ACME)". Only a ", <US state>" tag is removed, plus
// one 2-letter county token right before it; a name that would shrink under 3 characters is left alone.
export function stripLocationTag(value) {
  const raw = String(value == null ? '' : value);
  let s = raw.replace(TICKER_TAG, '');
  if (STATE_TAG.test(s)) {
    const noState = s.replace(STATE_TAG, '');
    const noCounty = noState.replace(COUNTY_TAG, '');
    s = noCounty.replace(/[^a-z0-9]/gi, '').length >= 3 ? noCounty : noState;
  }
  return s.replace(/[^a-z0-9]/gi, '').length >= 3 ? s : raw;
}

export function normalizeCompanyName(value) {
  let s = stripLocationTag(value).normalize('NFKD').replace(/[̀-ͯ]/g, '').toLowerCase();
  s = s.replace(/[™®©℠]/g, ' ').replace(/&/g, ' and ').replace(/[^a-z0-9]+/g, ' ').replace(/\s+/g, ' ').trim();
  if (!s) return '';
  let tokens = s.split(' ');
  if (tokens[0] === 'the' && tokens.length > 1) tokens = tokens.slice(1);
  // trailing legal words only, repeatedly ("... Company LLC"); never strip the whole name away
  while (tokens.length > 1 && LEGAL.has(tokens[tokens.length - 1])) tokens.pop();
  // "l l c" spelled out
  if (tokens.length > 3 && tokens.slice(-3).join(' ') === 'l l c') tokens = tokens.slice(0, -3);
  return tokens.join(' ');
}

// Two names with the same key are the same company name ("Pipe Market" and "PipeMarket" collide).
export const companyKey = (value) => normalizeCompanyName(value).replace(/ /g, '');

// Damerau-Levenshtein distance (adjacent swaps count as one edit: Enverus / Enervus).
export function editDistance(a, b) {
  const x = String(a); const y = String(b);
  if (x === y) return 0;
  const d = Array.from({ length: x.length + 1 }, (_, i) => [i, ...Array(y.length).fill(0)]);
  for (let j = 0; j <= y.length; j += 1) d[0][j] = j;
  for (let i = 1; i <= x.length; i += 1) {
    for (let j = 1; j <= y.length; j += 1) {
      const cost = x[i - 1] === y[j - 1] ? 0 : 1;
      d[i][j] = Math.min(d[i - 1][j] + 1, d[i][j - 1] + 1, d[i - 1][j - 1] + cost);
      if (i > 1 && j > 1 && x[i - 1] === y[j - 2] && x[i - 2] === y[j - 1]) d[i][j] = Math.min(d[i][j], d[i - 2][j - 2] + 1);
    }
  }
  return d[x.length][y.length];
}

// Near-match, not the same: a typo (one edit on names of 6+ letters, two on longer ones), one name starting the other ("Primary Vision" / "Primary Vision Frac Spread Count"), or mostly
// the same words. Never true for an exact collision (those are collapsed, not "similar").
export function isSimilar(a, b) {
  const ka = companyKey(a); const kb = companyKey(b);
  if (!ka || !kb || ka === kb) return false;
  const short = ka.length <= kb.length ? ka : kb;
  const long = ka.length <= kb.length ? kb : ka;
  if (short.length >= 6 && long.startsWith(short)) return true;
  if (Math.min(ka.length, kb.length) >= 6) {
    const d = editDistance(ka, kb);
    if (d <= 1 || (d <= 2 && d / Math.max(ka.length, kb.length) <= 0.3)) return true; // one slip, or two on a longer name (Enverus / Enervus)
  }
  const ta = new Set(normalizeCompanyName(a).split(' ')); const tb = new Set(normalizeCompanyName(b).split(' '));
  const inter = [...ta].filter((t) => tb.has(t)).length;
  const union = new Set([...ta, ...tb]).size;
  return union > 0 && ta.size > 1 && tb.size > 1 && inter / union >= 0.67;
}

// rows: [{ id, name, deleted_at?, merged_into_id?, created_at? }]. Live rows only; rows with the same key collapse into the oldest one (the survivor), which keeps
// the other ids in collapsedIds so a caller can show "2 records" or merge later. Order of first appearance is kept.
export function collapseCompanies(rows) {
  const groups = new Map();
  (rows || []).forEach((r) => {
    if (!r || r.deleted_at || r.merged_into_id) return;
    const k = companyKey(r.name);
    if (!k) return;
    if (!groups.has(k)) groups.set(k, []);
    groups.get(k).push(r);
  });
  return [...groups.values()].map((g) => {
    const sorted = [...g].sort((a, b) => String(a.created_at || '9999').localeCompare(String(b.created_at || '9999')) || String(a.id).localeCompare(String(b.id)));
    const [survivor, ...rest] = sorted;
    return { ...survivor, collapsed: rest.length, collapsedIds: rest.map((r) => r.id) };
  });
}

// What a typed term matches, and what is only close. matches: the key contains the term's key (survivors only); similar: close but not a match.
export function splitMatches(term, rows) {
  const t = companyKey(term);
  const live = collapseCompanies(rows);
  if (!t) return { matches: [], similar: [] };
  const matches = live.filter((r) => companyKey(r.name).includes(t));
  const have = new Set(matches.map((r) => r.id));
  const similar = live.filter((r) => !have.has(r.id) && isSimilar(term, r.name));
  return { matches, similar };
}
